import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Participant } from "../models/Participant";
import { ParticipantService } from "../services/ParticipantService";
import { useAuth } from "./AuthContext";
import { Event } from "../models/Event";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { EmploymentInfo } from "../models/EmploymentInfo";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ParticipantContextProps {
  participants: Participant[];
  events: Event[];
  selectedParticipant: Participant | null;
  setSelectedParticipant: (participantId: string) => void;
  createParticipant: (
    file: globalThis.File | null,
    participantData: Omit<
      Participant,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "updatedById"
      | "matterId"
      | "names"
    >,
  ) => Promise<string>;
  updateParticipant: (
    participantId: string,
    file: globalThis.File | null,
    updatedData: Partial<
      Omit<Participant, "_id" | "createdAt" | "updatedAt" | "updatedById">
    >,
  ) => Promise<void>;
  deleteParticipantById: (participantId: string) => void;

  // EmploymentInfo
  createEmployment: (
    participantId: string,
    employmentInfoData: Omit<EmploymentInfo, "_id" | "createdAt" | "updatedAt">,
  ) => Promise<string>;
  updateEmployment: (
    participantId: string,
    employmentInfoId: string,
    updatedData: Partial<
      Omit<EmploymentInfo, "_id" | "createdAt" | "updatedAt">
    >,
  ) => Promise<void>;
  deleteEmploymentById: (
    participantId: string,
    employmentInfoId: string,
  ) => void;

  enrichParticipant: (participantId: string, apolloId: string) => Promise<void>;

  loading: Loadings;
  error: Errors;
}

const ParticipantContext = createContext<ParticipantContextProps | undefined>(
  undefined,
);

export const useParticipant = (): ParticipantContextProps => {
  const context = useContext(ParticipantContext);
  if (!context) {
    throw new Error("useParticipant must be used within a ParticipantProvider");
  }
  return context;
};

export const ParticipantProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [participants, setParticipants] = useState<Participant[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedParticipant, setSelectedParticipant] =
    useState<Participant | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  useSocketEventByIds(
    "participants:changed",
    ["*"],
    (updatedDoc: Participant) => {
      setParticipants((prev) =>
        prev.map((p) => (p._id === updatedDoc._id ? updatedDoc : p)),
      );
      if (selectedParticipant?._id === updatedDoc._id) {
        setSelectedParticipant(updatedDoc);
      } else {
        fetchParticipants();
      }
    },
  );

  const fetchParticipants = async () => {
    if (!user || !currentMatterId) return;
    try {
      const list = await MatterService.getParticipants(currentMatterId);
      setParticipants(list);
      setErrors((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    setLoadings((prev) => ({ ...prev, list: true }));
    fetchParticipants();
  }, [user, currentMatterId]);

  const setSelectedParticipantById = async (participantId: string) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const participantData =
        await ParticipantService.getParticipant(participantId);
      const relatedEvents = await ParticipantService.getEvents(participantId);
      setSelectedParticipant(participantData);
      setEvents(relatedEvents);
      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      setSelectedParticipant(null);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createParticipant = async (
    file: globalThis.File | null,
    participantData: Omit<
      Participant,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "updatedById"
      | "matterId"
      | "names"
    >,
  ): Promise<string> => {
    const newId = await ParticipantService.createParticipant(file, {
      ...participantData,
      matterId: currentMatterId!,
      names: [participantData.name],
    });
    return newId;
  };

  const updateParticipant = async (
    participantId: string,
    file: globalThis.File | null,
    updatedData: Partial<
      Omit<Participant, "_id" | "createdAt" | "updatedAt" | "createdById">
    >,
  ) => {
    if (!currentMatterId) return;
    await ParticipantService.updateParticipant(participantId, file, {
      ...updatedData,
      matterId: currentMatterId!,
    });
    setSelectedParticipantById(participantId);
  };

  const deleteParticipantById = async (participantId: string) => {
    await ParticipantService.deleteParticipant(participantId);
    setParticipants((prev) => prev.filter((p) => p._id !== participantId));
    setSelectedParticipant(null);
    setEvents([]);
  };

  const createEmployment = async (
    participantId: string,
    employmentInfoData: Omit<EmploymentInfo, "_id" | "createdAt" | "updatedAt">,
  ): Promise<string> => {
    const newId = await ParticipantService.createEmployment(
      participantId,
      employmentInfoData,
    );
    return newId;
  };

  const updateEmployment = async (
    participantId: string,
    employmentInfoId: string,
    updatedData: Partial<
      Omit<EmploymentInfo, "_id" | "createdAt" | "updatedAt">
    >,
  ) => {
    await ParticipantService.updateEmployment(
      participantId,
      employmentInfoId,
      updatedData,
    );
  };
  const deleteEmploymentById = async (
    participantId: string,
    employmentInfoId: string,
  ) => {
    await ParticipantService.deleteEmployment(participantId, employmentInfoId);
  };

  const enrichParticipant = async (participantId: string, apolloId: string) => {
    if (!currentMatterId) return;
    await ParticipantService.enrichParticipant(participantId, apolloId);
    setSelectedParticipantById(participantId);
  };

  return (
    <ParticipantContext.Provider
      value={{
        participants,
        events,
        selectedParticipant,
        setSelectedParticipant: setSelectedParticipantById,
        createParticipant,
        updateParticipant,
        deleteParticipantById,

        createEmployment,
        updateEmployment,
        deleteEmploymentById,

        enrichParticipant,

        loading: loadings,
        error: errors,
      }}
    >
      {children}
    </ParticipantContext.Provider>
  );
};
